import { useState } from 'react';
import { V2Alert, V2Badge, V2Button, V2EmptyState } from '@sakhtar/design-system/react';
import { apiPost } from '../../../lib/apiClient.js';
import { PURPOSE_LABELS, faNum, previewBody, smsLength } from './smsModel.ts';
import { smsErrorText } from './smsApi.js';
import { SmsTemplateEditor } from './SmsTemplateEditor.js';
import type { SmsRoutineSpec, SmsStarter, SmsTemplate, SmsTemplatesResponse } from './smsTypes.ts';

export function SmsTemplatesTab(props: {
  readonly base: string;
  readonly data: SmsTemplatesResponse;
  readonly routineSpecs: readonly SmsRoutineSpec[];
  readonly canManage: boolean;
  readonly onChanged: () => void;
}) {
  const [editor, setEditor] = useState<{
    readonly template: SmsTemplate | null;
    readonly starter: SmsStarter | null;
    readonly nonce: number;
  } | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const active = props.data.items.filter((t) => t.status === 'active');
  const archived = props.data.items.filter((t) => t.status === 'archived');
  const routineTitle = (kind: string) =>
    props.routineSpecs.find((r) => r.kind === kind)?.title ?? kind;

  async function toggleArchive(t: SmsTemplate) {
    setBusyId(t.id);
    setError(null);
    try {
      await apiPost(
        `${props.base}/api/v1/crm/sms/templates/${t.id}/${t.status === 'active' ? 'archive' : 'restore'}`,
        {},
      );
      props.onChanged();
    } catch (err) {
      setError(smsErrorText(err));
    } finally {
      setBusyId(null);
    }
  }

  const open = (template: SmsTemplate | null, starter: SmsStarter | null = null) =>
    setEditor({ template, starter, nonce: Date.now() });

  const card = (t: SmsTemplate) => {
    const len = smsLength(t.body);
    return (
      <li
        key={t.id}
        className="sms-template"
        data-archived={t.status === 'archived' ? 'true' : undefined}
      >
        <div className="sms-template__head">
          <strong>{t.title}</strong>
          <V2Badge tone={t.purpose === 'marketing' ? 'warn' : 'info'}>
            {PURPOSE_LABELS[t.purpose]}
          </V2Badge>
        </div>
        <p className="sms-template__body">{previewBody(t.body, props.data.variables)}</p>
        <div className="sms-template__meta">
          <span>
            {faNum(len.characters)} نویسه، {faNum(len.segments)} پیامک
          </span>
          {t.usedBy.length > 0 ? (
            <span className="sms-template__used">
              در روال: {t.usedBy.map(routineTitle).join('، ')}
            </span>
          ) : null}
        </div>
        {props.canManage ? (
          <div className="sms-template__actions">
            {t.status === 'active' ? (
              <V2Button size="sm" variant="secondary" onClick={() => open(t)}>
                ویرایش
              </V2Button>
            ) : null}
            <V2Button
              size="sm"
              variant="ghost"
              disabled={busyId === t.id || (t.status === 'active' && t.usedBy.length > 0)}
              title={
                t.usedBy.length > 0 && t.status === 'active'
                  ? 'ابتدا قالب روال را عوض کنید'
                  : undefined
              }
              onClick={() => void toggleArchive(t)}
            >
              {t.status === 'active' ? 'بایگانی' : 'بازیابی'}
            </V2Button>
          </div>
        ) : null}
      </li>
    );
  };

  return (
    <section className="sms-tab" aria-label="قالب‌های پیامک">
      <div className="sms-tab__bar">
        <p>
          متن‌های آماده‌ای بسازید و در روال‌ها استفاده کنید. متغیرها هنگام ارسال با داده‌ٔ واقعی پر
          می‌شوند.
        </p>
        {props.canManage ? (
          <V2Button variant="primary" onClick={() => open(null)}>
            قالب جدید
          </V2Button>
        ) : null}
      </div>
      {error ? <V2Alert tone="danger" title={error} /> : null}

      {active.length === 0 ? (
        <V2EmptyState
          icon="plus"
          title="هنوز قالبی ندارید"
          description={
            props.canManage
              ? 'یک نمونه را انتخاب کنید و متنش را مطابق کسب‌وکار خود ویرایش کنید.'
              : 'برای ساخت قالب به دسترسی «مدیریت قالب پیامک» نیاز دارید.'
          }
        />
      ) : (
        <ul className="sms-template-grid">{active.map(card)}</ul>
      )}

      {props.canManage && active.length === 0 ? (
        <div className="sms-starters">
          <h3>نمونه‌های پیشنهادی</h3>
          <ul>
            {props.data.starters.map((s) => (
              <li key={s.kind}>
                <div>
                  <strong>{s.title}</strong>
                  <p>{previewBody(s.body, props.data.variables)}</p>
                </div>
                <V2Button size="sm" variant="secondary" onClick={() => open(null, s)}>
                  استفاده از نمونه
                </V2Button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {archived.length > 0 ? (
        <div className="sms-archived">
          <button type="button" className="sms-link" onClick={() => setShowArchived((v) => !v)}>
            {showArchived ? 'پنهان کردن' : 'نمایش'} بایگانی‌شده‌ها ({faNum(archived.length)})
          </button>
          {showArchived ? <ul className="sms-template-grid">{archived.map(card)}</ul> : null}
        </div>
      ) : null}

      {editor ? (
        <SmsTemplateEditor
          key={editor.nonce}
          base={props.base}
          open
          template={editor.template}
          starter={editor.starter}
          variables={props.data.variables}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            props.onChanged();
          }}
        />
      ) : null}
    </section>
  );
}
